import * as React from "react";
import { cx } from "../internal/cx.js";

export interface SummaryCardRow {
  /** Stable key. */
  id: string;
  /** Label noun phrase ("Coverage start"). */
  label: React.ReactNode;
  /** Already-formatted value. */
  value: React.ReactNode;
  /** Optional second line for additional context. */
  helperText?: React.ReactNode;
  /**
   * Visual emphasis. `"total"` renders the row as the emphasized grand-total
   * line — separated by a top rule, with a heavier label/value — for cost and
   * checkout summaries. Omit for a normal recap row.
   */
  emphasis?: "total";
}

export interface SummaryCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Section title — usually a noun ("Personal details"). */
  title: React.ReactNode;
  /** Optional small eyebrow above the title. */
  eyebrow?: React.ReactNode;
  /** Optional explanation under the title. */
  description?: React.ReactNode;
  /** Optional trailing action — usually an "Edit" Button. */
  action?: React.ReactNode;
  /** Data-driven rows (alternative to children). */
  rows?: SummaryCardRow[];
  /** Layout for rows. Default 'stacked' (label above value). */
  rowLayout?: "stacked" | "inline";
  /**
   * Card chrome (border + surface + padding). Default `true`. Set `false` when
   * the card sits inside another section surface and would read as a
   * card-in-a-card — it then renders flush, and the parent supplies dividers.
   */
  bordered?: boolean;
}

/**
 * Recap card for confirmation steps and review screens. Use after a multi-step
 * flow ("Review and submit") to surface the user's choices in a compact,
 * scannable block. Each row is a label : value pair; pass `rows` for the
 * data-driven shape, or `children` for arbitrary composition.
 */
export const SummaryCard = React.forwardRef<HTMLDivElement, SummaryCardProps>(function SummaryCard(
  {
    action,
    bordered = true,
    children,
    className,
    description,
    eyebrow,
    rowLayout = "stacked",
    rows,
    title,
    ...props
  },
  ref,
) {
  return (
    <section
      ref={ref as React.Ref<HTMLElement>}
      className={cx("cw-v2-summary-card", !bordered && "cw-v2-summary-card--flush", className)}
      {...(props as React.HTMLAttributes<HTMLElement>)}
    >
      <header className="cw-v2-summary-card__header">
        <div className="cw-v2-summary-card__heading">
          {eyebrow ? <div className="cw-v2-summary-card__eyebrow">{eyebrow}</div> : null}
          <div className="cw-v2-summary-card__title">{title}</div>
          {description ? (
            <div className="cw-v2-summary-card__description">{description}</div>
          ) : null}
        </div>
        {action ? <div className="cw-v2-summary-card__action">{action}</div> : null}
      </header>
      {rows && rows.length > 0 ? (
        <dl className={cx("cw-v2-summary-card__list", `cw-v2-summary-card__list--${rowLayout}`)}>
          {rows.map((row) => (
            <div
              key={row.id}
              className={cx(
                "cw-v2-summary-card__row",
                row.emphasis === "total" && "cw-v2-summary-card__row--total",
              )}
            >
              <dt className="cw-v2-summary-card__row-label">{row.label}</dt>
              <dd className="cw-v2-summary-card__row-value">
                {row.value}
                {row.helperText ? (
                  <div className="cw-v2-summary-card__row-helper">{row.helperText}</div>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {children ? <div className="cw-v2-summary-card__body">{children}</div> : null}
    </section>
  );
});
SummaryCard.displayName = "SummaryCard";
